import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
    Checkbox,
    FormControlLabel, Link,
    makeStyles,
    Theme, Typography,
} from '@material-ui/core';

import { Page } from '../../components/Page';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { FormGroup } from '../../components/FormGroup';
import { Block } from '../../components/Block';
import { CityAreaSelectGroup } from '../../components/CityAreaSelectGroup/CityAreaSelectGroup';
import { PageBody } from '../../components/PageBody';
import { UserDraftRequestData } from '../../api/userDraft';
import { api } from '../../api';
import { useLoading } from '../../units/loading';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { useAlert } from '../../units/alert';
import { Logo } from '../../components/Logo';
import { useLiff } from '../../units/liff';

type RegisterPageProps = {};

RegisterPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    root: {

    },
    title: {
        margin: theme.spacing(2, 1),
    },
}));

function RegisterPage(props: RegisterPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const { setIsLoading } = useLoading();
    const alert = useAlert();
    const { profile } = useLiff();
    const [confirmPassword, setConfirmPassword] = useState('');

    const [formData, setFormData] = useState<UserDraftRequestData>({
        line_uid: '',
        email: '',
        password: '',
        vat: '',
        company_name: '',
        customer_department: '',
        contact_address: '',
        name: '',
        phone: '',
    });

    useEffect(() => {
        if (profile) {
            setFormData((data) => ({
                ...data,
                line_uid: profile.userId,
            }));
        }
    }, [profile]);

    function handleRegisterClick() {
        setIsLoading(true);
        api.userDraft(formData)
            .then(() => {
                setIsLoading(false);
                alert.show({
                    type: 'success',
                    message: '註冊審核中。\n審核完成後會由系統發送通知Email給您，\n即可以帳密進行會員登入，謝謝。',
                    onConfirm: () => {
                        history.replace('/login');
                    },
                });
            })
            .catch(async (e) => {
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '註冊失敗',
                        message: errorData.msg,
                    });
                }
            });
    }

    function canSubmit() {
        return formData.email
            && formData.password
            && (formData.password === confirmPassword)
            && formData.company_name
            && formData.customer_department
            && formData.name
            && formData.phone;
    }

    function isConfirmPasswordPass() {
        return (confirmPassword && formData.password) && (confirmPassword === formData.password);
    }

    return (
        <Page className={classes.root}>
            <PageBody>
                <Logo />
                <Typography className={classes.title} variant="h5" align="center">註冊帳號</Typography>
                <FormGroup
                    title="帳號密碼"
                    actionElement={(
                        <div style={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
                            <Typography variant="caption"><span style={{ color: '#ef5350' }}> * </span>為必填項目</Typography>
                            <Typography variant="caption">請輸入正確資訊以提供完善服務</Typography>
                        </div>
                    )}
                >
                    <TextField
                        label="帳號(E-mail)"
                        value={formData.email}
                        onChange={(e) => setFormData({
                            ...formData,
                            email: e.target.value,
                        })}
                        helperText="建議使用公司/學校E-mail"
                        required
                    />
                    <TextField
                        label="密碼"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({
                            ...formData,
                            password: e.target.value,
                        })}
                        required
                    />
                    <TextField
                        label="確認密碼"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        error={Boolean(confirmPassword && formData.password) && !isConfirmPasswordPass()}
                        helperText={Boolean(confirmPassword && formData.password) && !isConfirmPasswordPass() ? '密碼與確認密碼不同' : ''}
                    />
                </FormGroup>
                <FormGroup title="單位資訊">
                    <TextField
                        label="公司/學校系所"
                        value={formData.company_name}
                        onChange={(e) => setFormData({
                            ...formData,
                            company_name: e.target.value,
                        })}
                        required
                    />
                    <TextField
                        label="公司/學校統編"
                        value={formData.vat}
                        onChange={(e) => setFormData({
                            ...formData,
                            vat: e.target.value,
                        })}
                    />
                    <TextField
                        label="部門單位/老師實驗室"
                        value={formData.customer_department}
                        onChange={(e) => setFormData({
                            ...formData,
                            customer_department: e.target.value,
                        })}
                        required
                    />
                    <TextField
                        label="地址"
                        value={formData.contact_address}
                        onChange={(e) => setFormData({
                            ...formData,
                            contact_address: e.target.value,
                        })}
                    />
                </FormGroup>
                <FormGroup title="基本資料">
                    <TextField
                        label="姓名"
                        value={formData.name}
                        onChange={(e) => setFormData({
                            ...formData,
                            name: e.target.value,
                        })}
                        required
                    />
                    <TextField
                        label="電話/手機"
                        value={formData.phone}
                        onChange={(e) => setFormData({
                            ...formData,
                            phone: e.target.value,
                        })}
                        required
                    />
                    <Button onClick={handleRegisterClick} disabled={!canSubmit()}>提出申請</Button>
                    <Block row justifyContent="center">
                        <Link component="a" onClick={() => history.replace('/login')}>點此返回登入頁</Link>
                    </Block>
                </FormGroup>
            </PageBody>
        </Page>
    );
}

export { RegisterPage };
